import { Flex, Tag, TagLabel, Text, Textarea } from '@chakra-ui/react'
import { ClinicalRationaleMatch } from '@hb/shared'
import { useCallback, useState } from 'react'
import { getPregnancyClinicalRationale } from '../../../../backend/functions/ai'
import { SolidActionButton } from '../../../Buttons'
import { DefaultModal } from '../../../Modals/DefaultModal'

export const TestFeatureModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [chiefComplaint, setChiefComplaint] = useState('')
  const [loading, setLoading] = useState(false)
  const [matches, setMatches] = useState<ClinicalRationaleMatch[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async () => {
    if (!chiefComplaint.trim()) return
    setLoading(true)
    setMatches(null)
    setError(null)
    try {
      const response = await getPregnancyClinicalRationale({ chiefComplaint })
      setMatches(response.data.matches)
    } catch (err) {
      setError((err as { message: string }).message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }, [chiefComplaint])

  const handleClose = useCallback(() => {
    setChiefComplaint('')
    setMatches(null)
    setError(null)
    onClose()
  }, [onClose])

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={handleClose}
      header="Test Clinical Rationale"
      headerProps={{
        bg: 'gray.50',
        _dark: { bg: 'gray.800', borderColor: 'gray.700' },
      }}
      size="lg"
      render={() => (
        <Flex direction="column" p={4} gap={3}>
          <Textarea
            placeholder="Enter chief complaint..."
            value={chiefComplaint}
            onChange={e => setChiefComplaint(e.target.value)}
            rows={4}
            resize="vertical"
          />
          <SolidActionButton
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={!chiefComplaint.trim()}
            loadingText="Analyzing...">
            Submit
          </SolidActionButton>
          {matches !== null && (
            <Flex direction="column" gap={2}>
              <Text fontWeight={600} fontSize="sm" color="gray.500">
                {matches.length > 0 ? 'Matched Categories' : 'No match found (low confidence)'}
              </Text>
              {matches.map(match => (
                <Flex
                  key={match.matchedCategoryName}
                  direction="column"
                  gap={1}
                  p={3}
                  bg="green.50"
                  _dark={{ bg: 'green.900' }}
                  borderRadius={4}>
                  <Flex align="center" gap={2}>
                    <Tag
                      size="sm"
                      colorScheme={
                        match.confidenceScore >= 0.8
                          ? 'green'
                          : match.confidenceScore >= 0.65
                            ? 'blue'
                            : 'orange'
                      }>
                      <TagLabel>
                        {match.matchedCategoryName} ({Math.round(match.confidenceScore * 100)}%)
                      </TagLabel>
                    </Tag>
                  </Flex>
                  <Text fontSize="sm" whiteSpace="pre-wrap">
                    {match.suggestion}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
          {error !== null && (
            <Text
              p={3}
              bg="red.50"
              _dark={{ bg: 'red.900' }}
              borderRadius={4}
              fontSize="sm"
              color="red.600">
              {error}
            </Text>
          )}
        </Flex>
      )}
    />
  )
}
